const DIRECT_SIGNER_COLUMN_TYPES = new Set(['email', 'people']);
const MIRROR_EMAIL_COLUMN_TYPE = 'mirror-email-column';

const SIGNER_MIRROR_COLUMN_ID_PREFIXES = [
  'email_',
  'people_',
  'person_',
  'multiple_person',
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseColumnSettings(
  settingsStr: string | null | undefined,
  settings?: unknown,
): unknown {
  if (isRecord(settings)) return settings;
  if (!settingsStr) return null;
  try {
    return JSON.parse(settingsStr);
  } catch {
    return null;
  }
}

export function mirroredColumnIdsFromSettings(
  settingsStr: string | null | undefined,
  settings?: unknown,
): string[] {
  const parsed = parseColumnSettings(settingsStr, settings);
  if (!isRecord(parsed)) return [];

  const displayed = parsed.displayed_linked_columns;
  if (Array.isArray(displayed)) {
    return displayed.flatMap((entry) => {
      if (!isRecord(entry)) return [];
      const columnIds = entry.column_ids;
      if (!Array.isArray(columnIds)) return [];
      return columnIds.filter(
        (columnId): columnId is string => typeof columnId === 'string',
      );
    });
  }

  if (!isRecord(displayed)) return [];

  return Object.values(displayed).flatMap((columnIds) => {
    if (!Array.isArray(columnIds)) return [];
    return columnIds.filter(
      (columnId): columnId is string => typeof columnId === 'string',
    );
  });
}

function isSignerMirroredColumnId(columnId: string): boolean {
  if (columnId === 'person') return true;
  return SIGNER_MIRROR_COLUMN_ID_PREFIXES.some((prefix) =>
    columnId.startsWith(prefix),
  );
}

export function isSignerEligibleMirrorColumn(
  settingsStr: string | null | undefined,
  settings?: unknown,
): boolean {
  const mirroredColumnIds = mirroredColumnIdsFromSettings(
    settingsStr,
    settings,
  );
  if (mirroredColumnIds.length === 0) return false;
  return mirroredColumnIds.some(isSignerMirroredColumnId);
}

export type SignerColumnLike = {
  type?: string | null;
  settings_str?: string | null;
  settingsStr?: string | null;
  settings?: unknown;
};

function columnSettingsStr(column: SignerColumnLike): string | null | undefined {
  return column.settings_str ?? column.settingsStr;
}

export function isSignerEligibleColumn(column: SignerColumnLike): boolean {
  if (!column.type) return false;
  if (DIRECT_SIGNER_COLUMN_TYPES.has(column.type)) return true;
  if (column.type === MIRROR_EMAIL_COLUMN_TYPE) return true;
  if (column.type !== 'mirror') return false;
  return isSignerEligibleMirrorColumn(
    columnSettingsStr(column),
    column.settings,
  );
}

export type ResolvedSignerColumnType =
  | 'email'
  | 'people'
  | 'mirror-email-column'
  | 'text';

export function resolveMondayColumnType(
  column: SignerColumnLike,
): ResolvedSignerColumnType | null {
  if (column.type === 'email' || column.type === 'people' || column.type === 'text') {
    return column.type;
  }
  if (column.type === MIRROR_EMAIL_COLUMN_TYPE) return MIRROR_EMAIL_COLUMN_TYPE;
  if (
    column.type === 'mirror' &&
    isSignerEligibleMirrorColumn(columnSettingsStr(column), column.settings)
  ) {
    return MIRROR_EMAIL_COLUMN_TYPE;
  }
  return null;
}
